import styled from 'styled-components';
import {Detail} from '@forecast-it/design-system';
import type {DashboardTotals} from '../types/dashboardTicket';
import {formatMedianDuration, formatPct, formatPercentagePoints, formatTotalCount} from '../utils/totalsFormatting';

const Bar = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[600]};
	padding: ${p => p.theme.sys.spacing[300]} ${p => p.theme.sys.spacing[400]};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
`;

const Metric = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

// `Detail` rather than `Text` for the figure: `Text` forwards only `data-cy` and
// drops className, so it can neither be styled nor carry a test id. Emphasis comes
// from weight and the main text colour, since Detail has no bold variant.
const MetricValue = styled(Detail)`
	font-weight: 600;
	color: ${p => p.theme.sys.color.text.content.main};
	white-space: nowrap;
`;

/**
 * The values every metric formatter reads from. Bundled so the five metrics can
 * stay one declarative list even though SLA achievement is sourced from the
 * gauge rather than the rollup shape.
 */
interface MetricValues {
	totals: DashboardTotals;
	slaAchievementRatePct: number | null;
}

/** The five rollups PRD §8.6 defines, in reading order. */
interface MetricSpec {
	testId: string;
	label: string;
	/** Optional native tooltip for cells whose scope differs from the rest of the row. */
	title?: string;
	value: (values: MetricValues) => string;
}

const SLA_SCOPE_HINT =
	'Of tickets resolved in this period, the share that breached neither SLA clock. ' +
	'Always measured by resolution date, regardless of the selected date field.';

const METRICS: readonly MetricSpec[] = [
	{testId: 'totals-total', label: 'Total', value: ({totals}) => formatTotalCount(totals.total)},
	{testId: 'totals-pct-resolved', label: 'Resolved', value: ({totals}) => formatPct(totals.pct_resolved)},
	{
		testId: 'totals-sla-achievement',
		label: 'SLA achievement',
		title: SLA_SCOPE_HINT,
		value: ({slaAchievementRatePct}) => formatPercentagePoints(slaAchievementRatePct),
	},
	{
		testId: 'totals-median-resolution',
		label: 'Median resolution',
		value: ({totals}) => formatMedianDuration(totals.median_resolution_time_seconds),
	},
	{
		testId: 'totals-median-first-response',
		label: 'Median first response',
		value: ({totals}) => formatMedianDuration(totals.median_first_response_time_seconds),
	},
];

interface Props {
	totals: DashboardTotals;
	/**
	 * The SLA gauge's rate as whole percentage points (0–100), or null when the
	 * gauge has no answer to give. Sourced from the analytics gauge so the bar
	 * and the gauge stacked above it show the same number — a single figure over
	 * a single cohort, incapable of disagreeing.
	 */
	slaAchievementRatePct: number | null;
}

/**
 * The totals row above the dashboard's ticket table (PRD §6.5 / §8.6).
 *
 * Every rollup always renders, showing a dash where there is nothing to compute,
 * rather than disappearing — a row whose fields come and go can't be scanned
 * against the group headers below it, and an absent median is itself information
 * ("nothing in scope has been resolved"). Same reasoning, and the same formatters,
 * as `DashboardGroupHeader`.
 *
 * A bar rather than a row inside the table: two of the five figures (SLA
 * achievement, median first response) have no column to sit under, so aligning
 * them with the table's columns would put numbers below unrelated headers.
 */
export function TicketDataTableTotalsRow({totals, slaAchievementRatePct}: Props) {
	const values: MetricValues = {totals, slaAchievementRatePct};
	return (
		<Bar data-testid="totals-row" data-cy="dashboard-totals-row">
			{METRICS.map(metric => (
				<Metric key={metric.testId} title={metric.title}>
					{/* `medium` is Detail's auxiliary-grey key; Detail has no `auxiliary`
					    key of its own, and an unknown key resolves to no colour at all. */}
					<Detail color="medium">{metric.label}</Detail>
					<MetricValue data-testid={metric.testId}>{metric.value(values)}</MetricValue>
				</Metric>
			))}
		</Bar>
	);
}
